// Los tres ruidos del taller: el martillo, el chisporroteo y el temple en el pilón.
//
//     node scripts/prueba_sonidosDeLaFragua.mjs        (desde frontend/)
//
// QUÉ SON Y QUÉ NO SON, porque esto podría deshacer lo del 27 de septiembre de 2026. Ese día
// Gabri pidió **unificar los sonidos de victoria y derrota de todos los minijuegos**, y se
// hicieron los cuatro de la casa —acertar, fallar, ganar, perder— en `sonidosDelJuego.mjs`. Un
// tono agudo sólo quiere decir «bien» cuando SIEMPRE quiere decir «bien», así que esos cuatro
// no se tocan y la Fragua los sigue usando igual que los demás.
//
// ESTOS TRES SON OTRA COSA: **no dicen cómo va, dicen dónde estás.** Son el ruido del sitio, y
// suenan ENCIMA de los cuatro, no en su lugar:
//
//   · el martillo, con el acierto: la pieza no aparece, se clava de un golpe;
//   · el chisporroteo, con el fallo: el hierro que se parte chirría;
//   · el temple, al acabar una palabra, que es el único momento del juego que **no tenía
//     ningún sonido**: la pieza acabada se mete en el pilón y suelta vapor.
//
// Y SON RUIDO, NO NOTAS: un martillo hecho con un oscilador es un pitido. El motor ya existía
// —`ruido()`, en `lib/sonidosDelMundo.js`, que `sound.js` importa desde hace tiempo—, así que
// aquí sólo están sus ajustes y con sus mismos nombres. Se escribió un segundo generador de
// ruido para esto y se deshizo en la misma tarde: dos copias de lo mismo que nada obliga a
// estar de acuerdo es la avería de siempre de esta casa.
//
// Lógica pura, sin audio.

/**
 * CADA RUIDO, EN CUATRO NÚMEROS Y UN FILTRO:
 *
 *   `dur`    cuánto dura, en segundos.
 *   `tipo`   qué se deja pasar del ruido blanco: `bandpass` recorta arriba y abajo —un golpe
 *            metálico—, `highpass` deja sólo lo agudo —un siseo—.
 *   `desde`/`hasta` por dónde empieza y acaba la frecuencia del filtro, en hercios. Que se
 *            muevan es lo que convierte una ráfaga plana en un gesto: el martillo cae.
 *   `gain`   lo alto que suena, y ninguno pasa de lo que suena un acierto.
 *   `q`      lo estrecho que es el filtro. Alto = metálico y con tono; bajo = aire.
 */
export const DEL_TALLER = {
  /**
   * EL MARTILLO. Corto y seco, con el filtro cayendo de agudo a grave: eso es un golpe. Con la
   * frecuencia subiendo sale un látigo, que es lo que hace `whoosh` y es justo lo contrario.
   */
  martillo: { dur: 0.16, tipo: "bandpass", desde: 2600, hasta: 620, gain: 0.1, q: 3.2 },
  /**
   * EL CHISPORROTEO de la pieza que se parte: más largo, agudo y sin tono. `q` bajo a propósito
   * —un chisporroteo no tiene nota— y `gain` la mitad que el martillo: **fallar no castiga**, y
   * un ruido fuerte al fallar diría lo contrario de lo que dice esta casa.
   */
  chisporroteo: { dur: 0.34, tipo: "highpass", desde: 3400, hasta: 1800, gain: 0.05, q: 0.7 },
  /**
   * EL TEMPLE en el pilón: el siseo largo del hierro al rojo entrando en agua. Es el más largo
   * de los tres porque es el que cierra una palabra, y lo que distingue un final de un paso más
   * no es el tono, es que ocupe. La misma regla que los cuatro de la casa.
   */
  temple: { dur: 0.62, tipo: "highpass", desde: 5200, hasta: 380, gain: 0.075, q: 0.6 },
};

/** Los tres, por su nombre. Lo lee su prueba y quien quiera recorrerlos sin escribirlos otra vez. */
export const LOS_TRES = Object.keys(DEL_TALLER);

/** Cuánto dura un ruido del taller, en segundos. Cero si no es de la casa. */
export const loQueDura = (cual) => (DEL_TALLER[cual] ? DEL_TALLER[cual].dur : 0);

/** Si el filtro baja de frecuencia: un golpe cae, un siseo se va. Ninguno de los tres sube. */
export const cae = (cual) => !!DEL_TALLER[cual]
  && DEL_TALLER[cual].hasta < DEL_TALLER[cual].desde;
